import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, X, RefreshCw } from "lucide-react";
import axios from "axios";
import { API } from "../utils/constants";
import { visitId } from "../utils/funnel";

// Signing in is the six digits of the email (or its link): no password. When it stands before the first
// result (`gate`), it says why: no result without an email (owner, 2026-10-27).
const AuthModal = ({ isOpen, onClose, onAuthenticated, gate = false }) => {
  const [email, setEmail] = useState("");
  const [step, setStep] = useState("email");
  const [code, setCode] = useState("");
  const [magicToken, setMagicToken] = useState("");
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);

  const requestCode = async () => {
    if (!email.trim() || !email.includes("@")) { setError("Enter a valid email"); return; }
    setError(""); setSending(true);
    try {
      const res = await axios.post(`${API}/auth/magic-link`, { email: email.trim().toLowerCase() });
      setMagicToken(res.data.token || "");
      setCode("");
      setStep("code");
    } catch (e) {
      setError(e?.response?.status === 429 ? "Too many emails — wait a few minutes and try again." : "Something went wrong. Try again.");
    } finally { setSending(false); }
  };

  const verifyCode = async (value) => {
    const digits = (value || "").replace(/\D/g, "");
    if (digits.length !== 6) { setError("The code has 6 digits"); return; }
    setStep("verifying"); setError("");
    try {
      const res = await axios.post(`${API}/auth/verify-code`, { email: email.trim().toLowerCase(), code: digits, visit: visitId() });
      onAuthenticated(res.data.email, res.data.session_token);
      onClose();
    } catch (e) {
      setError(e?.response?.data?.detail || "That code doesn't match.");
      setStep("code");
    }
  };

  const verifyToken = async () => {
    setStep("verifying");
    try {
      const res = await axios.get(`${API}/auth/verify/${magicToken}`);
      onAuthenticated(res.data.email, res.data.session_token);
      onClose();
    } catch (e) {
      setError("Link expired or already used.");
      setStep("email");
    }
  };

  if (!isOpen) return null;

  const title = step === "email" ? (gate ? "Where should we send your trip?" : "Save your trip")
    : step === "code" ? "Check your email" : "Verifying...";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{background:'rgba(0,0,0,0.5)'}}>
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-sm p-6" style={{background:'var(--zen-s1)',border:'1px solid var(--zen-border)',borderRadius:8}}>
        <div className="flex items-center justify-between mb-5">
          <h3 style={{ fontFamily: '"DM Sans", system-ui, sans-serif', fontWeight: 800, fontSize: 19, color:'var(--zen-text)'}}>{title}</h3>
          <button onClick={onClose} className="inline-flex items-center justify-center hover:opacity-70" style={{width:28,height:28,borderRadius:'50%',border:'1px solid var(--zen-border-h)',background:'transparent',color:'var(--zen-dim)'}}><X className="w-4 h-4" /></button>
        </div>

        {step === "email" && (
          <>
            <p className="text-sm mb-4" style={{color:'var(--zen-sub)'}}>
              {gate
                ? "Your trip lives in your account: everything you choose is saved there, no password needed. We'll email you a 6-digit code."
                : "Enter your email to save and access your itineraries anytime. We'll email you a 6-digit code."}
            </p>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)}
              onKeyDown={e => e.key === "Enter" && requestCode()}
              placeholder="your@email.com" autoFocus
              className="jf-field w-full mb-3"
              data-testid="auth-email-input" />
            {error && <p className="text-xs mb-3" style={{color:'var(--cat-nightlife-solid)'}}>{error}</p>}
            <button onClick={requestCode} disabled={sending} className="zen-btn zen-btn-primary w-full py-3" data-testid="auth-send-link-btn">
              <Mail className="w-4 h-4" /> {sending ? "Sending..." : "Send my code"}
            </button>
          </>
        )}

        {step === "code" && (
          <>
            <p className="text-sm mb-3" style={{color:'var(--zen-sub)'}}>
              We sent a 6-digit code to <strong style={{color:'var(--zen-text)'}}>{email}</strong>. Type it here — or open the link in the same email.
            </p>
            <input inputMode="numeric" autoComplete="one-time-code" maxLength={7} value={code} autoFocus
              onChange={e => { const v = e.target.value.replace(/\D/g, "").slice(0, 6); setCode(v); if (v.length === 6) verifyCode(v); }}
              onKeyDown={e => e.key === "Enter" && verifyCode(code)}
              placeholder="123456" className="jf-field w-full mb-3 text-center"
              style={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: 22, letterSpacing: "0.3em" }}
              data-testid="auth-code-input" />
            {error && <p className="text-xs mb-3" style={{color:'var(--cat-nightlife-solid)'}}>{error}</p>}
            <button onClick={() => verifyCode(code)} className="zen-btn zen-btn-primary w-full py-3" data-testid="auth-verify-code-btn">
              Continue
            </button>
            {/* Dev shortcut: the backend returns the token only when DEV_MODE is on (App.js reads ?magic_token= otherwise). */}
            {magicToken && (
              <button onClick={verifyToken} className="text-xs mt-3 hover:underline w-full" style={{color:'var(--zen-accent)'}}
                data-testid="auth-verify-link-btn">Dev shortcut — verify without the code</button>
            )}
            <p className="text-[11px] text-center mt-3" style={{color:'var(--zen-dim)'}}>
              The code expires in 30 minutes. Not there? Check your spam folder, or{" "}
              <button onClick={requestCode} className="underline" style={{background:'none',border:0,color:'var(--zen-dim)',cursor:'pointer',padding:0}}>send a new one</button>.
            </p>
          </>
        )}

        {step === "verifying" && (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="w-5 h-5 animate-spin" style={{color:'var(--zen-accent)'}} />
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default AuthModal;
